/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// formik components
import { Formik, Form } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// NewUser page components
import PlatformConfig from "views/platforms/new-platform/components/PlatformConfig";

// NewUser layout schemas for form and form feilds
import validations from "views/platforms/new-platform/schemas/validations";
import form from "views/platforms/new-platform/schemas/form";
import initialValues from "views/platforms/new-platform/schemas/initialValues";

import { postData, putData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

// sweetalert2 components
import Swal from "sweetalert2";

function getSteps() {
  return ["User Info"];
}

let filtersData = {};
let initFiltersData = {};
let errorVisibility = false;

function NewPlatform({
  signalCloseModal,
  id,
  platformName,
  platformExchange,
  vlcourtEmail,
  vlcourtPassword,
  queueIdentifier,
  routingKey,
  rmqsSelected,
  settleOnlyBets,
  validateBets,
  receiveSeperateStatistics,
  riskManagement,
  demo,
  hardProviderDown,
  nuRemove,
  compressMessage,
  traderView,
  promos,
  outrights,
  logo,
  masterId,
  showFilters,
  checkToClose,
}) {
  const [controller] = useSoftUIController();
  const { permissions } = controller;

  const [activeStep, setActiveStep] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errorLevel, setErrorLevel] = useState(0);
  const [editable, setEditable] = useState(false);
  const steps = getSteps();
  const { formId, formField } = form;
  const ref = useRef(null);
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  if (id > 0 && platformName) {
    initialValues.platformName = platformName;
    initialValues.platformExchange = platformExchange;
    initialValues.vlcourtEmail = vlcourtEmail;
    initialValues.vlcourtPassword = vlcourtPassword;
    initialValues.queueIdentifier = queueIdentifier;
    initialValues.routingKey = routingKey;
    initialValues.rmqsSelected = rmqsSelected;
    initialValues.settleOnlyBets = settleOnlyBets;
    initialValues.validateBets = validateBets;
    initialValues.receiveSeperateStatistics = receiveSeperateStatistics;
    initialValues.riskManagement = riskManagement;
    initialValues.demo = demo;
    initialValues.hardProviderDown = hardProviderDown;
    initialValues.nuRemove = nuRemove;
    initialValues.compressMessage = compressMessage;
    initialValues.traderView = traderView;
    initialValues.promos = promos;
    initialValues.outrights = outrights;
    initialValues.logo = logo;
  } else {
    initialValues.platformName = "";
    initialValues.platformExchange = "";
    initialValues.vlcourtEmail = "";
    initialValues.vlcourtPassword = "";
    initialValues.queueIdentifier = "";
    initialValues.routingKey = "";
    initialValues.rmqsSelected = [];
    initialValues.settleOnlyBets = false;
    initialValues.validateBets = false;
    initialValues.receiveSeperateStatistics = false;
    initialValues.riskManagement = false;
    initialValues.demo = false;
    initialValues.hardProviderDown = false;
    initialValues.nuRemove = false;
    initialValues.compressMessage = false;
    initialValues.traderView = false;
    initialValues.promos = false;
    initialValues.outrights = 3;
    initialValues.logo = "";
  }

  const submitForm = async (values, actions) => {
    await sleep(1000);

    if (id > 0 && masterId) {
      let objSent = { ...values };
      objSent.name = objSent.platformName;
      objSent.exchange_name = objSent.platformExchange;
      objSent.vlcourt_email = objSent.vlcourtEmail;
      objSent.vlcourt_password = objSent.vlcourtPassword;
      objSent.queue_identifier = objSent.queueIdentifier;
      objSent.routing_key =
        objSent.routingKey && objSent.routingKey != "" ? objSent.routingKey : null;
      objSent.rmqs_selected =
        objSent.rmqsSelected && objSent.rmqsSelected.length > 0
          ? objSent.rmqsSelected.join(",")
          : null;
      objSent.settle_only_bets = objSent.settleOnlyBets;
      objSent.validate_bets = !objSent.validateBets ? false : objSent.validateBets;
      objSent.receive_seperate_statistics = objSent.receiveSeperateStatistics;
      objSent.risk_management = objSent.riskManagement;
      objSent.demo = objSent.demo;
      objSent.hard_provider_down = objSent.hardProviderDown;
      objSent.nu_remove = !objSent.nuRemove ? false : objSent.nuRemove;
      objSent.compress_message = !objSent.compressMessage ? false : objSent.compressMessage;
      objSent.trader_view = !objSent.traderView ? false : objSent.traderView;
      objSent.promos = !objSent.promos ? false : objSent.promos;
      objSent.outrights = !objSent.outrights ? 3 : objSent.outrights;
      delete objSent.platformName;
      delete objSent.platformExchange;
      delete objSent.vlcourtEmail;
      delete objSent.vlcourtPassword;
      delete objSent.queueIdentifier;
      delete objSent.routingKey;
      delete objSent.rmqsSelected;
      delete objSent.settleOnlyBets;
      delete objSent.validateBets;
      delete objSent.receiveSeperateStatistics;
      delete objSent.riskManagement;
      delete objSent.hardProviderDown;
      delete objSent.nuRemove;
      delete objSent.compressMessage;
      delete objSent.traderView;
      delete objSent.logo;
      let updatePlatformEndpoint = `/api/v1/platforms/${id}`;
      let response = await putData(updatePlatformEndpoint, objSent, {
        Authorization: getAuthorizationHeader(),
      });

      if (!response) {
        let message =
          id > 0 && masterId
            ? `Something goes wrong on Platform ${objSent.name} update. Please try again later.`
            : `Something goes wrong on Platform ${objSent.name} creation. Please try again later.`;
        Swal.fire("Error!", message, "error");
        signalCloseModal(true, objSent.name);

        actions.setSubmitting(false);
        actions.resetForm();
        return;
      }
    }

    //console.log("Creation completed, please Update!");
    let setPlatformInitEndpoint =
      id > 0 && masterId
        ? `/api/v1/platforms/master-template/${masterId}`
        : `/api/v1/platforms/init`;

    let objSent = { ...values };
    objSent.name = objSent.platformName;
    objSent.exchange_name = objSent.platformExchange;
    objSent.vlcourt_email = objSent.vlcourtEmail;
    objSent.vlcourt_password = objSent.vlcourtPassword;
    objSent.queue_identifier = objSent.queueIdentifier;
    objSent.routing_key = objSent.routingKey == "" ? null : objSent.routingKey;
    objSent.rmqs_selected =
      objSent.rmqsSelected && objSent.rmqsSelected.length > 0
        ? objSent.rmqsSelected.join(",")
        : null;
    objSent.settle_only_bets = objSent.settleOnlyBets;
    objSent.validate_bets = !objSent.validateBets ? false : objSent.validateBets;
    objSent.receive_seperate_statistics = !objSent.receiveSeperateStatistics
      ? false
      : objSent.receiveSeperateStatistics;
    objSent.risk_management = !objSent.riskManagement ? false : objSent.riskManagement;
    objSent.demo = !objSent.demo ? false : objSent.demo;
    objSent.hard_provider_down = !objSent.hardProviderDown ? false : objSent.hardProviderDown;
    objSent.nu_remove =
      objSent.nuRemove == null || objSent.nuRemove == "" || !objSent.nuRemove
        ? false
        : objSent.nuRemove;
    objSent.compress_message =
      objSent.compressMessage == null || objSent.compressMessage == "" || !objSent.compressMessage
        ? false
        : objSent.compressMessage;
    objSent.trader_view =
      objSent.traderView == null || objSent.traderView == "" || !objSent.traderView
        ? false
        : objSent.traderView;
    objSent.promos =
      objSent.promos == null || objSent.promos == "" || !objSent.promos ? false : objSent.promos;
    objSent.outrights =
      objSent.outrights == null || objSent.outrights == "" || !objSent.outrights
        ? 3
        : objSent.outrights;

    delete objSent.platformName;
    delete objSent.platformExchange;
    delete objSent.vlcourtEmail;
    delete objSent.vlcourtPassword;
    delete objSent.queueIdentifier;
    delete objSent.routingKey;
    delete objSent.rmqsSelected;
    delete objSent.settleOnlyBets;
    delete objSent.validateBets;
    delete objSent.receiveSeperateStatistics;
    delete objSent.riskManagement;
    delete objSent.logo;
    delete objSent.hardProviderDown;
    delete objSent.nuRemove;
    delete objSent.compressMessage;
    delete objSent.traderView;

    for (const key in filtersData.providers) {
      if (Object.hasOwnProperty.call(filtersData.providers, key)) {
        const provider = filtersData.providers[key];
        let filterEntry = { provider: {}, sports: [], competitions: [] };
        filterEntry.provider.id = provider.id;
        filterEntry.provider.inplay = false;
        filterEntry.provider.pregame = false;
        for (const key in provider.sports) {
          if (Object.hasOwnProperty.call(provider.sports, key)) {
            const sport = provider.sports[key];
            let sportTopush = { id: 0, inplay: false, pregame: false };
            if (sport.inplay) filterEntry.provider.inplay = sport.selected ? sport.inplay : false;
            if (sport.pregame)
              filterEntry.provider.pregame = sport.selected ? sport.pregame : false;
            sportTopush.id = sport.id;
            sportTopush.inplay = sport.selected ? sport.inplay : false;
            sportTopush.pregame = sport.selected ? sport.pregame : false;
            sportTopush.default = true;

            // if (sport.selected) {
            //   filterEntry.provider.inplay = true;
            //   filterEntry.provider.pregame = true;
            //   sportTopush.inplay = true;
            //   sportTopush.pregame = true;
            //   sportTopush.default = true;
            // }
            filterEntry.sports.push(sportTopush);
          }
        }
        objSent.filters = objSent.filters ? objSent.filters : [];
        objSent.filters.push(filterEntry);
      }
    }

    //objSent.filters = filtersData;

    // eslint-disable-next-line no-alert
    //alert(JSON.stringify(objSent, null, 2));

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        if (showFilters) {
          if (JSON.stringify(initFiltersData) != JSON.stringify(filtersData)) {
            let response = await postData(setPlatformInitEndpoint, objSent, {
              Authorization: getAuthorizationHeader(),
            });

            if (!response) {
              Swal.clickDeny();
            } else {
              if (response.status === 200) {
                Swal.clickConfirm();
              }
            }
          } else {
            Swal.clickConfirm();
          }
        } else {
          Swal.clickConfirm();
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.isConfirmed) {
        let message =
          id > 0 && masterId
            ? `Platform ${objSent.name} Updated sucessfull`
            : `Platform ${objSent.name} Created sucessfull`;
        Swal.fire("Request Sent!", message, "success");
        signalCloseModal(true, objSent.name);

        actions.setSubmitting(false);
        actions.resetForm();
      }
      if (result.isDenied) {
        let message =
          id > 0 && masterId
            ? `Something goes wrong on Platform ${objSent.name} update. Please try again later.`
            : `Something goes wrong on Platform ${objSent.name} creation. Please try again later.`;
        Swal.fire("Error!", message, "error");
        signalCloseModal(true, objSent.name);

        actions.setSubmitting(false);
        actions.resetForm();
      }
    });

    return;
  };

  const handleSubmit = (values, actions) => {
    if (errorVisibility) {
      setShowError(true);
      return;
    }

    if ((filtersData.providers && filtersData.providers.length == 0) || !filtersData.providers) {
      setShowError(true);
      return;
    }

    setShowError(false);
    submitForm(values, actions);
  };

  const handleCancelCreation = () => {
    let cancelData = false;
    for (const key in ref.current.values) {
      if (Object.hasOwnProperty.call(ref.current.values, key)) {
        const element = ref.current.values[key];
        if (element != "") {
          cancelData = true;
          break;
        }
      }
    }

    if (cancelData) {
      Swal.fire({
        title: `${id > 0 && masterId ? "Cancel Platform Update?" : "Cancel Platform Creation?"}`,
        html: `${
          id > 0 && masterId
            ? "You are going to lose some data while Updating Platform. <br />Are you sure?"
            : "You are going to lose some data while Creating Platform. <br />Are you sure?"
        }`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          signalCloseModal(false, null);
          id = 0;
          masterId = null;
        } else {
          signalCloseModal(false, cancelData);
        }
      });
    } else {
      signalCloseModal(false, cancelData);
    }
  };

  const handleOk = (e) => {
    e.preventDefault();
    signalCloseModal(false, false);
  };

  const handleFilterData = (data) => {
    // if (Object.keys(filtersData).length == 0) {
    //   initFiltersData = JSON.parse(JSON.stringify(data));
    // }
    filtersData = data;
    errorVisibility = false;
    let checkpoints = filtersData.providers.length;
    let errorlevel = 0;
    for (const key in filtersData.providers) {
      if (Object.hasOwnProperty.call(filtersData.providers, key)) {
        const provider = filtersData.providers[key];
        for (const key in provider.sports) {
          if (Object.hasOwnProperty.call(provider.sports, key)) {
            const sport = provider.sports[key];
            if (sport.selected && (sport.inplay || sport.pregame)) {
              checkpoints--;
              break;
            }
            if (sport.selected && !sport.inplay && !sport.pregame) {
              errorlevel = 2;
              checkpoints++;
            }
          }
        }
      }
    }

    if (checkpoints > 0) {
      errorVisibility = true;
      if (errorlevel == 0) errorlevel = 1;
    }
    setErrorLevel(errorlevel);

    if (errorlevel) setShowError(true);
    else setShowError(false);

    // if (showError && !errorVisibility) setShowError(false);
  };

  useEffect(() => {
    if (checkToClose) handleCancelCreation();
  }, [checkToClose]);

  useEffect(() => {
    if (
      permissions.length > 0 &&
      (permissions.indexOf("management-platforms-edit") > -1 || permissions.indexOf("super") > -1)
    ) {
      setEditable(true);
    }
  }, [permissions]);

  useEffect(() => {
    return () => {
      filtersData = {};
    };
  }, []);

  return (
    <Grid container justifyContent="center" sx={{ height: "100%", width: "100%" }}>
      <Grid item sx={{ height: "100%", width: "100%" }}>
        <Formik
          innerRef={ref}
          initialValues={initialValues}
          validationSchema={currentValidation}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form id={formId} autoComplete="off">
              <Card sx={{ height: "100%" }}>
                <SoftBox p={2}>
                  <SoftBox className="platform-config-main-body">
                    <PlatformConfig
                      formData={{
                        values,
                        touched,
                        formField,
                        errors,
                      }}
                      allowCreate={showFilters}
                      handleData={handleFilterData}
                      id={id}
                      errorVisibility={showError}
                      errorLevel={errorLevel}
                    />
                  </SoftBox>
                  <SoftBox>
                    <Grid container justifyContent="right">
                      <Grid item sx={{ display: editable ? "" : "none" }}>
                        <SoftButton variant="outlined" color="info" onClick={handleCancelCreation}>
                          Cancel
                        </SoftButton>
                      </Grid>
                      <Grid item sx={{ display: editable ? "" : "none" }}>
                        <SoftButton type="submit" variant="gradient" color="dark">
                          save
                        </SoftButton>
                      </Grid>
                      <Grid item sx={{ display: !editable ? "" : "none" }}>
                        <SoftButton variant="outlined" color="info" onClick={(e) => handleOk(e)}>
                          ok
                        </SoftButton>
                      </Grid>
                    </Grid>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}

NewPlatform.propTypes = {
  signalCloseModal: PropTypes.func,
  id: PropTypes.number,
  platformName: PropTypes.string,
  vlcourtEmail: PropTypes.string,
  vlcourtPassword: PropTypes.string,
  queueIdentifier: PropTypes.string,
  routingKey: PropTypes.string,
  rmqsSelected: PropTypes.array,
  platformExchange: PropTypes.string,
  settleOnlyBets: PropTypes.bool,
  validateBets: PropTypes.bool,
  receiveSeperateStatistics: PropTypes.bool,
  riskManagement: PropTypes.bool,
  demo: PropTypes.bool,
  hardProviderDown: PropTypes.bool,
  nuRemove: PropTypes.bool,
  compressMessage: PropTypes.bool,
  traderView: PropTypes.bool,
  promos: PropTypes.bool,
  outrights: PropTypes.number,
  logo: PropTypes.string,
  masterId: PropTypes.number,
  showFilters: PropTypes.bool,
  checkToClose: PropTypes.bool,
};

export default NewPlatform;
